import { AspectRatio, Box, Flex, HStack, Tag } from '@chakra-ui/react';
import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import { LabelPromo, Text } from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  numberWithSeparator,
} from '@diamond/shared/utils';
import {
  type ProductWithPromo,
  formatDiscountAmountAbbreviated,
  processVoucherData,
} from '@diamond/sol-buyer/utils';
import { dinero, toDecimal } from 'dinero.js';

import { CheckIconVoucher } from '../../carts/Icons';
import { Image } from '../../common/image';
import { CheckboxOutlineBlankIcon } from '../icons';
import type {
  AppliedVoucherInfo,
  ProductItemProps,
  VoucherProductDiscount,
} from './types';

type Product = ProductItemProps['product'];

type PromoFlags = {
  hasPromoDiscount: boolean;
  hasPromoCashback: boolean;
  hasPromoFreeGoods: boolean;
};

type PricingPresentation = {
  /** Whether the UI should render "discount mode" (badge above + strikethrough original). */
  showDiscountDetails: boolean;
  originalUnitPrice: number;
  finalUnitPrice: number;
  totalPrice: number;
  /** Discount badge label to display (e.g. `20%` or `-100rb`). */
  discountBadgeLabel: string;
};

/**
 * Returns true when a note is a meaningful, user-provided string.
 */
const isValidNote = (note: unknown): note is string => {
  if (!note || typeof note !== 'string') return false;
  const trimmed = note.trim();
  return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined';
};

export interface ProductItemRefactoredProps {
  /** Product with promo/voucher data bound by `bindPromoData`. */
  product: ProductItemProps['product'];
  /** Division-level promos (kept for API compatibility; currently unused in this component). */
  promos: ProductItemProps['promos'];
  /** Voucher discount data from reward_by_item_code lookup */
  voucherDiscount?: VoucherProductDiscount;
  /** Applied voucher info for reward_type */
  voucherInfo?: AppliedVoucherInfo;
}

/**
 * Converts a magnified stock quantity (e.g. 1000) into its display value (e.g. 1).
 */
const getDisplayQuantity = (quantity: Product['quantity']): number =>
  Number(
    toDecimal(
      dinero({
        amount: Number(quantity ?? 0),
        currency: STOCK_UI,
      })
    )
  );

/**
 * Derives promo flags from a product row (promo data is bound by `bindPromoData`).
 */
const getPromoFlags = (product: Product): PromoFlags => ({
  hasPromoDiscount: product.hasDiscount || false,
  hasPromoCashback: product.hasCashback || false,
  hasPromoFreeGoods: product.free_good_data !== null,
});

/**
 * Voucher discount can come from either:
 * - `voucherDiscount` prop (reward_by_item_code lookup), or
 * - `product.voucher_discounts` fallback.
 */
const getEffectiveVoucherDiscount = (
  product: Product,
  voucherDiscount?: VoucherProductDiscount
): VoucherProductDiscount | undefined => {
  if (voucherDiscount) return voucherDiscount;

  return (
    product as typeof product & {
      voucher_discounts?: VoucherProductDiscount[];
    }
  ).voucher_discounts?.[0];
};

/**
 * A voucher discount is considered present when it has either:
 * - a positive percent discount, or
 * - a positive fixed discount amount.
 */
const getHasVoucherDiscount = (
  effectiveVoucherDiscount?: VoucherProductDiscount
): boolean =>
  Boolean(effectiveVoucherDiscount) &&
  (((effectiveVoucherDiscount?.discount_percent ?? 0) || 0) > 0 ||
    ((effectiveVoucherDiscount?.discount_amount ?? 0) || 0) > 0);

/**
 * Voucher free goods presence is determined via the shared voucher-processing utility
 * (it normalizes missing/nullable arrays).
 */
const getHasVoucherFreeGoods = (product: Product): boolean => {
  const voucherData = processVoucherData(
    product as unknown as ProductWithPromo
  );
  return voucherData.hasVoucherFreeGoods;
};

/**
 * Computes the unit-price and total-price presentation, including discount badge label.
 *
 * Precedence rule: voucher discount overrides promo discount when present.
 */
const getPricingPresentation = ({
  product,
  promoFlags,
  effectiveVoucherDiscount,
  hasVoucherDiscount,
}: {
  product: Product;
  promoFlags: PromoFlags;
  effectiveVoucherDiscount?: VoucherProductDiscount;
  hasVoucherDiscount: boolean;
}): PricingPresentation => {
  let originalUnitPrice = product.price || 0;
  let finalUnitPrice = product.price || 0;
  let totalPrice = product.subtotal || product.original_subtotal || 0;
  let discountBadgeLabel = '';
  let promoDiscountAmount = 0;

  if (
    promoFlags.hasPromoDiscount &&
    product.compiled_discount &&
    product.compiled_discount.length > 0
  ) {
    const discountPromo = product.compiled_discount[0];
    finalUnitPrice = discountPromo.price_after_discount || product.price || 0;
    originalUnitPrice = product.price || 0;
    promoDiscountAmount = originalUnitPrice - finalUnitPrice;

    if (originalUnitPrice > 0 && promoDiscountAmount > 0) {
      const percent = Math.round(
        (promoDiscountAmount / originalUnitPrice) * 100
      );
      discountBadgeLabel = `${percent}%`;
    }
  }

  if (hasVoucherDiscount && effectiveVoucherDiscount) {
    const voucherQuantity = (effectiveVoucherDiscount.quantity || 0) / 1000;
    if (
      voucherQuantity > 0 &&
      effectiveVoucherDiscount.final_subtotal !== undefined
    ) {
      finalUnitPrice =
        effectiveVoucherDiscount.final_subtotal / voucherQuantity;
      originalUnitPrice =
        (effectiveVoucherDiscount.original_subtotal || 0) / voucherQuantity;
    }

    totalPrice = effectiveVoucherDiscount.final_subtotal || totalPrice;

    if ((effectiveVoucherDiscount.discount_percent ?? 0) > 0) {
      discountBadgeLabel = `${effectiveVoucherDiscount.discount_percent}%`;
    } else if ((effectiveVoucherDiscount.discount_amount ?? 0) > 0) {
      discountBadgeLabel = formatDiscountAmountAbbreviated(
        effectiveVoucherDiscount.discount_amount
      );
    }
  }

  const showDiscountDetails =
    (promoFlags.hasPromoDiscount && promoDiscountAmount > 0) ||
    hasVoucherDiscount;

  return {
    showDiscountDetails,
    originalUnitPrice,
    finalUnitPrice,
    totalPrice,
    discountBadgeLabel,
  };
};

/**
 * Renders a single checkout-new product row with promo and voucher indicators.
 *
 * Unit price layout rules:
 * - Discount (promo/voucher): badge row above, original price strikethrough.
 * - No discount: promo/voucher badges render inline to the left of the unit price.
 */
export function ProductItem(props: ProductItemRefactoredProps) {
  const { product, voucherDiscount } = props;

  const displayQuantity = getDisplayQuantity(product.quantity);
  const promoFlags = getPromoFlags(product);

  const effectiveVoucherDiscount = getEffectiveVoucherDiscount(
    product,
    voucherDiscount
  );
  const hasVoucherDiscount = getHasVoucherDiscount(effectiveVoucherDiscount);
  const hasVoucherFreeGoods = getHasVoucherFreeGoods(product);

  const pricing = getPricingPresentation({
    product,
    promoFlags,
    effectiveVoucherDiscount,
    hasVoucherDiscount,
  });

  const hasInlineBadges =
    promoFlags.hasPromoCashback ||
    promoFlags.hasPromoFreeGoods ||
    hasVoucherFreeGoods;
  const showInlinePromoRow = hasInlineBadges && !pricing.showDiscountDetails;

  return (
    <Flex
      gap={{ base: 3, md: 4 }}
      p={{ base: 3, md: 4 }}
      borderRadius="md"
      bg="white"
      opacity={product.is_not_processed ? 0.5 : 1}
      flexDir="row"
    >
      <Flex flexShrink={0} justify="flex-start" align="flex-start">
        <AspectRatio
          w={{ base: '80px', md: '112px' }}
          ratio={1 / 1}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
          overflow="hidden"
        >
          <Image
            fill
            src={product.product_images_medium?.[0] || ''}
            alt={product.desc}
            style={{
              backgroundColor: 'white',
              objectFit: 'contain',
            }}
          />
        </AspectRatio>
      </Flex>

      <Flex flex={1} flexDir="column" gap={2} minW={0}>
        <Flex justify="space-between" align="center">
          <Box flex={1} minW={0} maxW={{ base: '300px', md: '100%' }}>
            <Text
              fontSize={{ base: 'sm', md: 'md' }}
              fontWeight="normal"
              color="gray.900"
              noOfLines={1}
            >
              {product.brand_desc} • {product.desc}
            </Text>
            {product.item_code && (
              <Text fontSize={{ base: 'xs', md: 'sm' }} color="#999999" mt={1}>
                {product.item_code}
              </Text>
            )}
          </Box>
          <Flex
            direction="column"
            align="flex-end"
            justify="flex-start"
            flexShrink={0}
            ml={2}
          >
            <Text
              fontSize={{ base: 'xs', md: 'sm' }}
              fontWeight="bold"
              color="gray.900"
              lineHeight="1.2"
            >
              x{numberWithSeparator(displayQuantity)}
            </Text>
            <Text
              fontSize={{ base: 'xs', md: 'sm' }}
              fontWeight="normal"
              color="#999999"
              lineHeight="1.2"
            >
              {product.sales_unit_desc || product.pack_unit || 'pcs'}
            </Text>
          </Flex>
        </Flex>

        <Flex justify="space-between" align="start">
          <Text fontSize={{ base: 'xs', md: 'sm' }} color="#999999">
            Harga satuan
          </Text>
          <Box textAlign="right">
            {pricing.showDiscountDetails ? (
              <>
                <HStack
                  spacing={2}
                  justify="flex-end"
                  mb={1}
                  data-testid="unit-price-discount-row"
                >
                  {hasVoucherDiscount ? (
                    <CheckIconVoucher
                      boxSize={{ base: '16px', md: '20px' }}
                      flexShrink={0}
                    />
                  ) : null}
                  {pricing.discountBadgeLabel ? (
                    <Box
                      px="6px"
                      py="2px"
                      bg="#FFDADA"
                      borderRadius="4px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text
                        fontWeight="700"
                        fontSize="10px"
                        lineHeight="1.2"
                        color="#FF3B30"
                      >
                        {pricing.discountBadgeLabel}
                      </Text>
                    </Box>
                  ) : null}
                  <Text
                    fontSize={{ base: 'xs', md: 'sm' }}
                    color="gray.500"
                    textDecor="line-through"
                  >
                    {buyerCurrencyFormatter(pricing.originalUnitPrice)}
                  </Text>
                </HStack>
                <Text fontSize="sm" color="#131415">
                  {buyerCurrencyFormatter(pricing.finalUnitPrice)}
                </Text>
              </>
            ) : null}

            {showInlinePromoRow ? (
              <HStack
                spacing={2}
                justify="flex-end"
                flexWrap="wrap"
                data-testid="unit-price-inline-row"
              >
                {hasVoucherFreeGoods ? (
                  <CheckIconVoucher
                    boxSize={{ base: '16px', md: '20px' }}
                    flexShrink={0}
                  />
                ) : null}
                {promoFlags.hasPromoFreeGoods || hasVoucherFreeGoods ? (
                  <LabelPromo type="free-item" />
                ) : null}
                {promoFlags.hasPromoCashback ? (
                  <LabelPromo type="cashback" />
                ) : null}
                <Text fontSize="sm" color="gray.900">
                  {buyerCurrencyFormatter(pricing.finalUnitPrice)}
                </Text>
              </HStack>
            ) : null}

            {!pricing.showDiscountDetails && !showInlinePromoRow ? (
              <Text fontSize="sm" color="gray.900">
                {buyerCurrencyFormatter(pricing.finalUnitPrice)}
              </Text>
            ) : null}
          </Box>
        </Flex>

        <Flex justify="space-between" align="center">
          <Text fontSize={{ base: 'xs', md: 'sm' }} color="#999999">
            Total harga
          </Text>
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            fontWeight="bold"
            color="gray.900"
          >
            {buyerCurrencyFormatter(pricing.totalPrice)}
          </Text>
        </Flex>

        {isValidNote(product.note) && (
          <Flex
            gap={2}
            align="start"
            px={0}
            py={1}
            bg="orange.50"
            borderRadius="md"
            alignItems="center"
          >
            <CheckboxOutlineBlankIcon
              sx={{
                fontSize: '20px',
                color: '#8E8E93',
              }}
            />
            <Text
              fontSize={{ base: 'xs', md: 'sm' }}
              color="orange.800"
              flex={1}
            >
              {product.note}
            </Text>
          </Flex>
        )}

        {product.is_not_processed && (
          <Tag
            size="sm"
            colorScheme="gray"
            variant="solid"
            borderRadius="full"
            alignSelf="flex-start"
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            Dibatalkan
          </Tag>
        )}
      </Flex>
    </Flex>
  );
}
